import { Button } from "./ui";
import { useTranslation } from "react-i18next";

/**
 * Previous/Next over a list whose length is known.
 *
 * The operator console has a component of the same name doing a related job,
 * and the two are deliberately not the same component. That one has to cope
 * with a total the server may not be willing to count, and shows a range of
 * offsets. This one is told the total up front, so it can say which page of
 * how many — whether the list is held in memory (the model catalogue arrives
 * complete: `GET /v1/models` is OpenAI-compatible, the spec has no pagination
 * in it, and every SDK expects the whole list) or fetched a page at a time
 * from an endpoint that counts what it holds, as a job's run history is.
 *
 * Nothing renders when there is one page. Two disabled buttons under a range
 * that restates the count above the table is furniture standing in for a
 * control.
 */
export function Pagination({
  total,
  perPage,
  page,
  onChange,
}: {
  total: number;
  perPage: number;
  /** Already clamped by `paginate` — this component does no bounds work. */
  page: number;
  onChange: (page: number) => void;
}) {
  const { t } = useTranslation("common");
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages < 2) return null;

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-4 py-3">
      {/*
        Announced, because pressing Next changes the table above without moving
        focus — a screen reader is otherwise told nothing happened.
      */}
      <p className="text-xs text-fg-subtle" aria-live="polite">
        {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </p>
      <div className="flex gap-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
        >
          {t("pagination.previous")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={page === pages}
          onClick={() => onChange(page + 1)}
        >
          {t("pagination.next")}
        </Button>
      </div>
    </div>
  );
}
